import { z } from 'zod';

// ─────────────────────────────────────────────
//   Auth
// ─────────────────────────────────────────────

export const SessionUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120),
  picture: z.string().url().optional(),
  isAdmin: z.boolean(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

export const GoogleAuthRequestSchema = z.object({
  credential: z.string().min(20), // Google ID token JWT
});

// ─────────────────────────────────────────────
//   Lessons
// ─────────────────────────────────────────────

export const LessonStepSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('read'),
    body: z.string(),
    keyPoints: z.array(z.string()).max(8),
    analogy: z.string().optional(),
  }),
  z.object({
    type: z.literal('explore'),
    instruction: z.string(),
    targets: z.array(z.string()).min(1).max(20), // names of clickable elements
  }),
  z.object({
    type: z.literal('quiz'),
    question: z.string(),
    options: z.array(z.string()).length(4), // always A/B/C/D
    // correctIndex deliberately NOT here — server-only
  }),
  z.object({
    type: z.literal('fill-in'),
    question: z.string(),
    placeholder: z.string().optional(),
    // expected answer NOT here — server-only
  }),
  z.object({
    type: z.literal('sandbox'),
    instruction: z.string(),
    targetPath: z.array(z.string()).min(1), // e.g., ['Teams Directory', 'Connect', 'Engineering']
    /** True when the lesson author prefixed the sandbox callout with 🧪.
     *  The Lesson UI then surfaces an "Open my sandbox in Notion" button
     *  that deep-links to the user's per-user training-sandbox page. */
    useSandboxPage: z.boolean().default(false),
  }),
]);
export type LessonStep = z.infer<typeof LessonStepSchema>;

export const LessonMetaSchema = z.object({
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
  title: z.string(),
  level: z.number(),
  order: z.number().int().nonnegative(),
  description: z.string(),
  estimatedMinutes: z.number().int().positive(),
  tags: z.array(z.string()),
});
export type LessonMeta = z.infer<typeof LessonMetaSchema>;

export const LessonSchema = LessonMetaSchema.extend({
  steps: z.array(LessonStepSchema).min(1),
});
export type Lesson = z.infer<typeof LessonSchema>;

// ─────────────────────────────────────────────
//   Progress
// ─────────────────────────────────────────────

export const StepCompletionRequestSchema = z.object({
  stepIndex: z.number().int().nonnegative(),
  // For quiz: 0..3.  For fill-in: the typed string. For others: omitted.
  answer: z.union([z.number().int().min(0).max(3), z.string().max(1000)]).optional(),
});

export const StepCompletionResponseSchema = z.object({
  correct: z.boolean(),
  explanation: z.string().optional(),
});

export const LessonCompleteResponseSchema = z.object({
  lessonsCompleted: z.number().int().nonnegative(),
  streakDays: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative(),
  level: z.number(),
  nextLessonSlug: z.string().nullable(),
  bridgeText: z.string().optional(),
});

export const UserProgressSchema = z.object({
  lessonsCompleted: z.number().int().nonnegative(),
  currentLevel: z.number(),
  currentLessonSlug: z.string().nullable(),
  streakDays: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative(),
  lastLessonDate: z.string().date().nullable(),
  /** Slugs of lessons the user has completed. Used by the dashboard to derive
   *  per-level "X / N" counts without an extra Notion query. */
  completedSlugs: z.array(z.string()).default([]),
});
export type UserProgress = z.infer<typeof UserProgressSchema>;

// ─────────────────────────────────────────────
//   Q&A
// ─────────────────────────────────────────────

export const QAStatusSchema = z.enum(['open', 'answered', 'closed']);
export type QAStatus = z.infer<typeof QAStatusSchema>;

export const QAQuestionSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  asker: z.object({ email: z.string().email(), name: z.string() }),
  status: QAStatusSchema,
  tags: z.array(z.string()),
  levelLink: z.number().nullable(),
  upvotes: z.number().int().nonnegative(),
  replyCount: z.number().int().nonnegative(),
  acceptedAnswer: z
    .object({
      body: z.string(),
      answerer: z.object({ email: z.string().email(), name: z.string() }),
      answeredAt: z.string(),
    })
    .nullable(),
  createdAt: z.string(),
});

export const CreateQuestionRequestSchema = z.object({
  title: z.string().min(8).max(200),
  body: z.string().min(0).max(5000),
  tags: z.array(z.string()).max(5),
  levelLink: z.number().optional(),
});

export const CreateReplyRequestSchema = z.object({
  body: z.string().min(1).max(5000),
});

export const AcceptAnswerRequestSchema = z.object({
  replyId: z.string(),
});

// ─────────────────────────────────────────────
//   Wishes
// ─────────────────────────────────────────────

export const WishStatusSchema = z.enum(['open', 'planned', 'shipped', 'declined']);

export const CreateWishRequestSchema = z.object({
  title: z.string().min(8).max(200),
  body: z.string().min(0).max(5000),
});

// ─────────────────────────────────────────────
//   AI Plan
// ─────────────────────────────────────────────

export const PlanLevelTagSchema = z.enum(['skip', 'priority', 'useful']);

export const PlanLevelSchema = z.object({
  level: z.number(),
  tag: PlanLevelTagSchema,
  reason: z.string().min(20).max(400),
  recommendedLessonSlugs: z.array(z.string()).optional(),
});

export const AIPlanSchema = z.object({
  intro: z.string().min(20).max(400),
  totalRecommended: z.number().int().nonnegative(),
  levels: z.array(PlanLevelSchema),
  generatedAt: z.string(),
});
export type AIPlan = z.infer<typeof AIPlanSchema>;

export const GeneratePlanRequestSchema = z.object({
  persona: z.string().min(10).max(2000),
});

export const GetPlanResponseSchema = z.object({
  plan: AIPlanSchema.nullable(),
  remainingToday: z.number().int().min(0),
});
export type GetPlanResponse = z.infer<typeof GetPlanResponseSchema>;

// ─────────────────────────────────────────────
//   Errors (consistent shape)
// ─────────────────────────────────────────────

export const APIErrorSchema = z.object({
  error: z.string(),
  detail: z.string().optional(),
  code: z.string().optional(),
});
export type APIError = z.infer<typeof APIErrorSchema>;
